import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Login from './presentation/routes/auth/login';
import { AuthProvider } from './contexts/AuthContext';
import { SelectedCivProvider } from './contexts/SelectedCivContext';
import { ErrorBoundary } from 'react-error-boundary';
import { TOKEN_KEY } from './lib/api/auth';
import { Toaster } from './components/ui/toaster';
import { Fallback } from './components/Fallback';
import { Suspense, lazy } from 'react';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PWAInstallPrompt } from './components/pwa/PWAInstallPrompt';
import { OfflineIndicator } from './components/pwa/OfflineIndicator';

// Chargement paresseux pour les pages
const PortfolioPage = lazy(() => import('./presentation/routes/portfolio/PortfolioPage'));
const HomePage = lazy(() => import('./presentation/routes/home/HomePage'));
const DealFlowPage = lazy(() => import('./presentation/routes/deal-flow/DealFlowPage'));

/**
 * Composant racine de l'application.
 *
 * Ce composant initialise les fournisseurs de contexte nécessaires (QueryClient, AuthProvider)
 * et configure le routage de l'application à l'aide de React Router.
 * Il inclut également une gestion globale des erreurs avec ErrorBoundary et un système de notifications (Toaster).
 *
 * @returns {JSX.Element} Le composant App rendu.
 */
function App() {
  // Initialise le client React Query pour la gestion des données et la mise en cache.
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Configuration par défaut pour les requêtes React Query :
        staleTime: 1000 * 60 * 5, // Les données sont considérées comme "fraîches" pendant 5 minutes.
        gcTime: 1000 * 60 * 30, // Les données non utilisées sont supprimées du cache après 30 minutes.
        retry: 1, // Réessaie les requêtes échouées 1 fois.
        refetchOnWindowFocus: false, // Ne pas automatiquement rafraîchir les données lorsque la fenêtre reprend le focus.
      },
    },
  });

  /**
   * Gère les erreurs capturées par ErrorBoundary.
   *
   * @param {Error} error L'erreur capturée.
   * @param {React.ErrorInfo} info Informations sur le composant qui a causé l'erreur.
   */
  const handleError = (error: Error, info: React.ErrorInfo) => {
    // Idéalement, envoyer ces informations à un service de logging d'erreurs (ex: Sentry, LogRocket)
    console.error('ErrorBoundary caught an error:', error, info);
    // Afficher un message à l'utilisateur ou effectuer d'autres actions de secours.
  };

  return (
    // ErrorBoundary intercepte les erreurs JavaScript partout dans son arbre de composants enfants,
    // enregistre ces erreurs et affiche une UI de secours.
    <ErrorBoundary FallbackComponent={Fallback} onError={handleError}>
      {/* QueryClientProvider connecte le client React Query à l'application. */}
      <QueryClientProvider client={queryClient}>
        {/* AuthProvider gère l'état d'authentification et le rend disponible dans toute l'application. */}
        <ErrorBoundary
          FallbackComponent={({ error }) => (
            <div className="min-h-screen flex items-center justify-center bg-red-50 p-4">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur détectée</h2>
                <p className="mb-4 text-gray-700">Une erreur s'est produite dans l'application :</p>
                <div className="bg-gray-100 p-3 rounded overflow-auto max-h-60 mb-4">
                  <pre className="text-sm text-red-800">{error.message}</pre>
                  <pre className="text-xs text-gray-600 mt-2">{error.stack}</pre>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Recharger l'application
                </button>
              </div>
            </div>
          )}
        >
          {/* Router doit englober AuthProvider pour que useNavigate fonctionne dans AuthProvider */}
          <Router>
            <AuthProvider>
              {/* Toaster est utilisé pour afficher des notifications (toast messages). */}
              <Toaster />
              {/* Composants PWA pour l'installation, les mises à jour et le statut de connexion */}
              <PWAInstallPrompt />
              <OfflineIndicator />

              <SelectedCivProvider>
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-600 text-lg">Chargement de l'application...</p>
                      </div>
                    </div>
                  }
                >
                  <Routes>
                    {/* Page de connexion - */}
                    <Route
                      path="/login"
                      element={
                        localStorage.getItem(TOKEN_KEY) ? (
                          <Navigate to="/portfolio" replace />
                        ) : (
                          <Login />
                        )
                      }
                    />

                    {/* Routes protégées */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/portfolio" element={<PortfolioPage />} />
                      <Route path="/deal_flow" element={<DealFlowPage />} />
                    </Route>

                    {/* Redirection par défaut pour les utilisateurs authentifiés */}
                    <Route
                      path="/"
                      element={
                        localStorage.getItem(TOKEN_KEY) ? (
                          <Navigate to="/portfolio" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Route de secours pour les URL non reconnues */}
                    <Route
                      path="*"
                      element={
                        <div className="min-h-screen flex items-center justify-center bg-gray-50">
                          <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                              Page non trouvée
                            </h2>
                            <p className="text-gray-600 mb-6">L'URL demandée n'existe pas.</p>
                            <button
                              onClick={() => (window.location.href = '/')}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Retour à l'accueil
                            </button>
                          </div>
                        </div>
                      }
                    />
                  </Routes>
                </Suspense>
              </SelectedCivProvider>
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
